import { useMarkWhatsNew, type WhatsNewMarks } from "@vidario/api-client";
import {
  groupByMonth,
  hasUnread,
  initialNotified,
  initialSeen,
  inlineMarkdown,
  noticeNotes,
  splitRecent,
  visibleNotes,
  type MonthOfNotes,
  type NoteKind,
  type ReleaseNote,
  type User,
} from "@vidario/domain";
import { Button, Drawer } from "@vidario/ui";
import { Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { Link } from "react-router";

import { RELEASE_NOTES } from "../platform/whats-new-notes";

// "Neuigkeiten" (ADR 0005): Release Notes for recruiters and admins, opened
// only on request from the TopNav, plus a one-time Release Notice under the
// button when a release brings something this user hasn't been told about.

const KIND: Record<NoteKind, { label: string; classes: string }> = {
  new: { label: "Neu", classes: "bg-primary-50 text-primary-700" },
  improved: { label: "Verbessert", classes: "bg-info-50 text-info-700" },
  fixed: { label: "Behoben", classes: "bg-success-50 text-success-700" },
};

// The Release Notice names at most this many notes; the rest are "+ N weitere".
const NOTICE_TITLES = 2;

const BADGE = "inline-flex h-5 items-center rounded px-1.5 text-[11px] font-semibold";

function neuigkeiten(count: number): string {
  return count === 1 ? "1 Neuigkeit" : `${count} Neuigkeiten`;
}

// Today in Berlin (Vidario's market), as YYYY-MM-DD.
function berlinToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(new Date());
}

function monthLabel(month: string): string {
  return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(
    new Date(`${month}-01T12:00:00`),
  );
}

function NoteLink({
  href,
  onNavigate,
  className,
  children,
}: {
  href: string;
  onNavigate: () => void;
  className: string;
  children: string;
}): ReactElement {
  if (href.startsWith("/")) {
    return (
      <Link to={href} onClick={onNavigate} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

const LINK = "text-primary-700 underline underline-offset-2";

function MonthSection({
  month,
  onNavigate,
  focusHeading,
}: {
  month: MonthOfNotes;
  onNavigate: () => void;
  focusHeading?: boolean;
}): ReactElement {
  return (
    <section aria-labelledby={`whats-new-${month.month}`}>
      <h3
        ref={focusHeading ? (heading) => heading?.focus() : undefined}
        tabIndex={focusHeading ? -1 : undefined}
        id={`whats-new-${month.month}`}
        className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary focus:outline-none"
      >
        {monthLabel(month.month)}
      </h3>
      <ul className="space-y-5">
        {month.notes.map((note) => (
          <li key={note.id}>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`${BADGE} ${KIND[note.kind].classes}`}>{KIND[note.kind].label}</span>
              {note.audience === "admin" && (
                <span className={`${BADGE} bg-neutral-100 text-neutral-700`}>Nur für Admins</span>
              )}
            </div>
            <p className="mt-1.5 text-sm font-semibold">{note.title}</p>
            <p className="mt-1 text-sm text-text-secondary">
              {inlineMarkdown(note.body).map((part, i) =>
                part.type === "bold" ? (
                  <strong key={i} className="font-semibold text-text-primary">
                    {part.text}
                  </strong>
                ) : part.type === "link" ? (
                  <NoteLink key={i} href={part.href} onNavigate={onNavigate} className={LINK}>
                    {part.text}
                  </NoteLink>
                ) : (
                  <span key={i}>{part.text}</span>
                ),
              )}
            </p>
            {note.link && (
              <NoteLink
                href={note.link}
                onNavigate={onNavigate}
                className={`mt-1 inline-block text-sm ${LINK}`}
              >
                Mehr erfahren
              </NoteLink>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function WhatsNew({ user }: { user: User }): ReactElement {
  const { mutateAsync } = useMarkWhatsNew();
  const [open, setOpen] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  // Where this user starts, decided once when the app loads (the layout
  // remounts this per user). A missing set gets its first-visit baseline:
  // seen = the 30-day rule; notified = a new user has been told about
  // everything, an existing one about what they've seen.
  const [start] = useState(() => {
    const seen = user.seenWhatsNew ?? initialSeen(RELEASE_NOTES, berlinToday());
    const notified =
      user.notifiedWhatsNew ??
      initialNotified(
        RELEASE_NOTES.map((note) => note.id),
        user.seenWhatsNew,
      );
    return { seen, notified };
  });
  // Then the dot follows what we mark.
  const [seen, setSeen] = useState<string[]>(start.seen);
  // The Release Notice: at app load, never mid-session; null = no notice.
  const [notice, setNotice] = useState<ReleaseNote[] | null>(() => {
    const announce = noticeNotes(
      visibleNotes(RELEASE_NOTES, user.role),
      start.seen,
      start.notified,
    );
    return announce.length > 0 ? announce : null;
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);

  const visible = visibleNotes(RELEASE_NOTES, user.role);
  const unread = hasUnread(visible, seen);

  // The dot follows the server's answer, merged in (the sets only grow), so
  // an older answer arriving last can't bring it back. A failed save keeps
  // the dot; the next load shows it again anyway.
  function mark(marks: WhatsNewMarks) {
    if (!marks.seenWhatsNew?.length && !marks.notifiedWhatsNew?.length) return;
    mutateAsync(marks)
      .then((me) => setSeen((current) => [...new Set([...current, ...(me.seenWhatsNew ?? [])])]))
      .catch(() => undefined);
  }

  // Store a first-visit baseline on the server, once.
  useEffect(() => {
    mark({
      ...(user.seenWhatsNew === null && { seenWhatsNew: start.seen }),
      ...(user.notifiedWhatsNew === null && { notifiedWhatsNew: start.notified }),
    });
    // Once per mount; later /me/ updates are our own marks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc closes the notice (not the panel's job: the Drawer handles its own).
  useEffect(() => {
    if (!notice || open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissNotice();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Opening marks every note this user can see as seen, earlier months
  // included, and counts as having read the notice.
  function openPanel() {
    setOpen(true);
    setShowEarlier(false);
    mark({
      ...(unread && { seenWhatsNew: visible.map((note) => note.id) }),
      ...(notice && { notifiedWhatsNew: notice.map((note) => note.id) }),
    });
    setNotice(null);
  }

  // × and Esc only close the notice: the notes stay unread, so the dot stays.
  function dismissNotice() {
    if (!notice) return;
    if (noticeRef.current?.contains(document.activeElement)) buttonRef.current?.focus();
    mark({ notifiedWhatsNew: notice.map((note) => note.id) });
    setNotice(null);
  }

  const months = groupByMonth(visible);
  const { recent, earlier } = splitRecent(months);
  const oldest = months.at(-1);
  const more = notice ? notice.length - NOTICE_TITLES : 0;
  const closePanel = () => setOpen(false);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={openPanel}
        aria-label={unread ? "Neuigkeiten, ungelesen" : "Neuigkeiten"}
        className="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {/* The dot is paired with the accessible name, never colour alone. */}
        <span className="relative">
          <Sparkles size={16} aria-hidden />
          {unread && (
            <span
              data-testid="whats-new-dot"
              aria-hidden
              className="absolute -right-1 -top-1 size-2 rounded-full bg-primary-500 ring-2 ring-neutral-0"
            />
          )}
        </span>
        <span className="hidden xl:inline">Neuigkeiten</span>
      </button>

      {/* Present from the start so the one announcement is heard. */}
      <p role="status" className="sr-only">
        {notice ? `${neuigkeiten(notice.length)} in Vidario` : ""}
      </p>

      {/* Right after the button in the tab order; never takes focus. Under
          the button from sm up; on phones across the width under the header. */}
      {notice && (
        <div
          ref={noticeRef}
          role="region"
          aria-labelledby="whats-new-notice-title"
          className="fixed inset-x-4 top-[calc(4.5rem+var(--anonym-bar-offset,0px))] z-40 rounded-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 border border-border-default bg-neutral-0 p-4 shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
        >
          <div className="flex items-start justify-between gap-3">
            <p id="whats-new-notice-title" className="m-0 text-sm font-semibold">
              {notice.length === 1 ? "Neu in Vidario" : `${neuigkeiten(notice.length)} in Vidario`}
            </p>
            <button
              type="button"
              onClick={dismissNotice}
              aria-label="Hinweis schließen"
              className="-m-1.5 inline-flex rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <X size={16} />
            </button>
          </div>
          <ul className="mt-2 space-y-1 text-sm">
            {notice.slice(0, NOTICE_TITLES).map((note) => (
              <li key={note.id} className="flex gap-2">
                <span aria-hidden className="text-text-tertiary">
                  •
                </span>
                <span>
                  {note.title}
                  {note.audience === "admin" && (
                    <span className="text-text-tertiary"> (für Admins)</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {more > 0 && <p className="mt-1 text-sm text-text-tertiary">+ {more} weitere</p>}
          <Button size="sm" className="mt-3" onClick={openPanel}>
            Neuigkeiten ansehen
          </Button>
        </div>
      )}

      <Drawer
        open={open}
        onOpenChange={(next) => !next && closePanel()}
        returnFocusRef={buttonRef}
        title="Neuigkeiten"
        description="Was sich in Vidario für dich geändert hat."
      >
        {months.length === 0 ? (
          <p className="text-sm text-text-tertiary">Noch keine Neuigkeiten.</p>
        ) : (
          <div className="space-y-8">
            {recent.map((month) => (
              <MonthSection key={month.month} month={month} onNavigate={closePanel} />
            ))}
            {earlier.length > 0 && !showEarlier && (
              <button
                type="button"
                onClick={() => setShowEarlier(true)}
                className={`text-sm font-medium ${LINK} rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
              >
                Frühere Monate anzeigen
              </button>
            )}
            {showEarlier &&
              earlier.map((month, i) => (
                <MonthSection
                  key={month.month}
                  month={month}
                  onNavigate={closePanel}
                  focusHeading={i === 0}
                />
              ))}
            {oldest && (earlier.length === 0 || showEarlier) && (
              <p className="border-t border-border-default pt-4 text-xs text-text-tertiary">
                Das waren alle Neuigkeiten seit {monthLabel(oldest.month)}.
              </p>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
